// Shared utility for message composition logic
// Used by both ConfirmMessagesStep and WeeklyDunningOpStep

export interface TemplateMessage {
  category: string;
  reminder_type: string;
  proposed_channel?: {
    value: string;
  };
  proposed_msg_template?: {
    message: string;
    message_multiple_invoices?: string;
    message_wa_template_name?: string;
    message_multiple_invoices_wa_template_name?: string;
    subject_line?: string;
    reasonings?: string[];
  };
}

export interface CustomerData {
  name: string;
  xero_contact_id: string;
  invoice_metadata?: {
    outstanding_invoice_count: number;
    overdue_receivable: number;
  };
}

export interface InvoiceData {
  invoice_number: string;
  date_at: string;
  due_date_at: string;
  amount_due: string;
  status: string;
}

// Generate WhatsApp-friendly invoice table for multiple overdue invoices
export const generateWhatsAppInvoiceTable = (invoices: InvoiceData[]) => {
  if (!invoices || invoices.length === 0) {
    return 'No invoices found.';
  }

  // Filter to only outstanding invoices (not paid and with amount due > 0)
  const outstandingInvoices = invoices.filter(invoice =>
    invoice.status !== 'PAID' && parseFloat(invoice.amount_due || '0') > 0
  );

  if (outstandingInvoices.length === 0) {
    return 'No outstanding invoices found.';
  }

  // Calculate days overdue for each outstanding invoice
  const today = new Date();
  const invoicesWithOverdue = outstandingInvoices.map(invoice => {
    const dueDate = invoice.due_date_at ? new Date(invoice.due_date_at) : null;
    const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
    return {
      ...invoice,
      daysOverdue
    };
  });

  // Sort by days overdue (most overdue first)
  const sortedInvoices = invoicesWithOverdue.sort((a, b) => b.daysOverdue - a.daysOverdue);

  let result = '';
  
  // WhatsApp-friendly format
  result += '📋 *OVERDUE INVOICES:*\n';
  result += '━━━━━━━━━━━━━━━━━━━━\n';
  
  sortedInvoices.forEach((invoice, index) => {
    const invoiceNumber = invoice.invoice_number || 'N/A';
    const amount = `$${parseFloat(invoice.amount_due || '0').toFixed(2)}`;
    const daysOverdue = invoice.daysOverdue === 0 ? '0' : invoice.daysOverdue.toString();
    
    result += `• *${invoiceNumber}* - ${amount} (${daysOverdue} days overdue)\n`;
  });
  
  result += '━━━━━━━━━━━━━━━━━━━━';

  return result;
};

// Generate invoice table for template (plain text version for WhatsApp)
export const generateInvoiceTable = (invoices: InvoiceData[]) => {
  if (!invoices || invoices.length === 0) {
    return 'No invoices found.';
  }

  // Filter to only outstanding invoices (not paid and with amount due > 0)
  const outstandingInvoices = invoices.filter(invoice =>
    invoice.status !== 'PAID' && parseFloat(invoice.amount_due || '0') > 0
  );

  if (outstandingInvoices.length === 0) {
    return 'No outstanding invoices found.';
  }

  // Calculate days overdue for each outstanding invoice and separate them
  const today = new Date();
  const invoicesWithOverdue = outstandingInvoices.map(invoice => {
    // Use due_date_at for due date calculation
    const dueDate = invoice.due_date_at ? new Date(invoice.due_date_at) : null;
    const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
    return {
      ...invoice,
      daysOverdue
    };
  });

  // Sort all invoices by days overdue (overdue first, then current)
  const allInvoices = invoicesWithOverdue.sort((a, b) => b.daysOverdue - a.daysOverdue);

  let result = '';

  // Create single STATEMENT OF ACCOUNT table
  result += '\nSTATEMENT OF ACCOUNT:\n';
  result += '┌─────────────────┬──────────────┬──────────────┬─────────────┬─────────────┐\n';
  result += '│ Invoice Number  │ Invoice Date │ Due Date     │ Amount      │ Days Overdue│\n';
  result += '├─────────────────┼──────────────┼──────────────┼─────────────┼─────────────┤\n';

  allInvoices.forEach(invoice => {
    const invoiceNumber = (invoice.invoice_number || 'N/A').padEnd(15);

    // Use date_at for invoice date and convert UTC to browser timezone
    const invoiceDate = invoice.date_at ?
      new Date(invoice.date_at).toLocaleDateString(undefined, {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }) : 'N/A';

    // Use due_date_at for due date and convert UTC to browser timezone
    const dueDate = invoice.due_date_at ?
      new Date(invoice.due_date_at).toLocaleDateString(undefined, {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }) : 'N/A';

    const amount = `$${parseFloat(invoice.amount_due || '0').toFixed(2)}`.padEnd(11);
    // Show "0" for current invoices (daysOverdue === 0), actual days for overdue
    const daysOverdue = invoice.daysOverdue === 0 ? '0' : invoice.daysOverdue.toString();
    const daysOverduePadded = daysOverdue.padEnd(11);

    result += `│ ${invoiceNumber} │ ${invoiceDate.padEnd(12)} │ ${dueDate.padEnd(12)} │ ${amount} │ ${daysOverduePadded} │\n`;
  });

  result += '└─────────────────┴──────────────┴──────────────┴─────────────┴─────────────┘\n';

  return result;
};

// Generate HTML invoice table for email (Gmail-compatible responsive table)
export const generateInvoiceTableHTML = (invoices: InvoiceData[]) => {
  if (!invoices || invoices.length === 0) {
    return '<p>No invoices found.</p>';
  }

  // Filter to only outstanding invoices (not paid and with amount due > 0)
  const outstandingInvoices = invoices.filter(invoice =>
    invoice.status !== 'PAID' && parseFloat(invoice.amount_due || '0') > 0
  );

  if (outstandingInvoices.length === 0) {
    return '<p>No outstanding invoices found.</p>';
  }

  // Calculate days overdue for each outstanding invoice
  const today = new Date();
  const invoicesWithOverdue = outstandingInvoices.map(invoice => {
    const dueDate = invoice.due_date_at ? new Date(invoice.due_date_at) : null;
    const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
    return {
      ...invoice,
      daysOverdue
    };
  });

  // Sort all invoices by days overdue (overdue first, then current)
  const allInvoices = invoicesWithOverdue.sort((a, b) => b.daysOverdue - a.daysOverdue);

  // Format dates
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString(undefined, {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  };

  // Build HTML table with inline styles for Gmail compatibility
  // Using a robust table that works on both desktop and mobile
  // Gmail-compatible: all styles inline, using table attributes for better compatibility
  let html = '<div style="margin: 10px 0;">';
  html += '<h3 style="margin: 0 0 6px 0; font-size: 14px; font-weight: bold; color: #333333; font-family: Afacad, sans-serif;">STATEMENT OF ACCOUNT:</h3>';
  
  // Wrap table in a container for mobile horizontal scrolling
  html += '<div style="overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%;">';
  
  // Use table with auto layout and minimum widths for better mobile display
  html += '<table width="100%" cellpadding="4" cellspacing="0" border="1" style="width: 100%; min-width: 600px; border-collapse: collapse; border: 1px solid #dddddd; font-family: Afacad, sans-serif; font-size: 14px; background-color: #ffffff;">';
  
  // Table header - use minimum widths to prevent squeezing on mobile
  html += '<thead>';
  html += '<tr style="background-color: #f5f5f5;">';
  html += '<th style="padding: 8px 8px; text-align: left; border: 1px solid #dddddd; font-weight: bold; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; white-space: nowrap; min-width: 120px;">Invoice Number</th>';
  html += '<th style="padding: 8px 8px; text-align: left; border: 1px solid #dddddd; font-weight: bold; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; white-space: nowrap; min-width: 100px;">Invoice Date</th>';
  html += '<th style="padding: 8px 8px; text-align: left; border: 1px solid #dddddd; font-weight: bold; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; white-space: nowrap; min-width: 100px;">Due Date</th>';
  html += '<th style="padding: 8px 8px; text-align: right; border: 1px solid #dddddd; font-weight: bold; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; white-space: nowrap; min-width: 100px;">Amount</th>';
  html += '<th style="padding: 8px 8px; text-align: center; border: 1px solid #dddddd; font-weight: bold; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; white-space: nowrap; min-width: 100px;">Days Overdue</th>';
  html += '</tr>';
  html += '</thead>';
  
  // Table body
  html += '<tbody>';
  allInvoices.forEach((invoice, index) => {
    const bgColor = index % 2 === 0 ? '#ffffff' : '#f9f9f9';
    const invoiceNumber = invoice.invoice_number || 'N/A';
    const invoiceDate = formatDate(invoice.date_at);
    const dueDate = formatDate(invoice.due_date_at);
    const amount = `$${parseFloat(invoice.amount_due || '0').toFixed(2)}`;
    const daysOverdue = invoice.daysOverdue === 0 ? '0' : invoice.daysOverdue.toString();
    
    html += `<tr style="background-color: ${bgColor};">`;
    html += `<td style="padding: 6px 8px; border: 1px solid #dddddd; word-break: break-word; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; min-width: 120px;">${escapeHtml(invoiceNumber)}</td>`;
    html += `<td style="padding: 6px 8px; border: 1px solid #dddddd; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; white-space: nowrap; min-width: 100px;">${escapeHtml(invoiceDate)}</td>`;
    html += `<td style="padding: 6px 8px; border: 1px solid #dddddd; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; white-space: nowrap; min-width: 100px;">${escapeHtml(dueDate)}</td>`;
    html += `<td style="padding: 6px 8px; border: 1px solid #dddddd; text-align: right; font-weight: 500; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; white-space: nowrap; min-width: 100px;">${escapeHtml(amount)}</td>`;
    html += `<td style="padding: 6px 8px; border: 1px solid #dddddd; text-align: center; color: #333333; font-size: 14px; font-family: Afacad, sans-serif; white-space: nowrap; min-width: 100px;">${escapeHtml(daysOverdue)}</td>`;
    html += '</tr>';
  });
  html += '</tbody>';
  html += '</table>';
  html += '</div>'; // End scroll wrapper
  html += '</div>';

  return html;
};

// Helper function to escape HTML
const escapeHtml = (text: string): string => {
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
};

// Escape regex special characters in placeholder strings
const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Compose customer name: prefer picName, format as "picName (xeroDirectContactName)" if both exist
const composeCustomerName = (picName?: string, xeroDirectContactName?: string, fallbackName?: string): string => {
  if (picName && picName.trim()) {
    if (xeroDirectContactName && xeroDirectContactName.trim() && xeroDirectContactName !== picName.trim()) {
      return `${picName.trim()} (${xeroDirectContactName.trim()})`;
    }
    return picName.trim();
  }
  return xeroDirectContactName && xeroDirectContactName.trim() ? xeroDirectContactName.trim() : (fallbackName || 'Customer');
};

// Compose message with customer data
// If channel is 'email', returns HTML version; otherwise returns plain text
export const composeMessage = (template: TemplateMessage, customer: CustomerData, invoices: InvoiceData[], organizationName?: string, channel?: 'email' | 'whatsapp', picName?: string, xeroDirectContactName?: string) => {
  if (!template.proposed_msg_template?.message || !customer) {
    return template.proposed_msg_template?.message || '';
  }

  // Check if this is an overdue reminder and if we should use multiple invoices template
  const isOverdueReminder = template.reminder_type === 'overdue_reminder';
  const outstandingInvoices = invoices.filter(invoice =>
    invoice.status !== 'PAID' && parseFloat(invoice.amount_due || '0') > 0
  );
  
  // Count overdue invoices
  const today = new Date();
  const overdueInvoices = outstandingInvoices.filter(invoice => {
    const dueDate = invoice.due_date_at ? new Date(invoice.due_date_at) : null;
    const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
    return daysOverdue > 0;
  });

  // Use message_multiple_invoices if available and customer has multiple overdue invoices
  const useMultipleInvoicesTemplate = isOverdueReminder && 
    template.proposed_msg_template?.message_multiple_invoices && 
    overdueInvoices.length > 1;

  let message = useMultipleInvoicesTemplate 
    ? template.proposed_msg_template.message_multiple_invoices 
    : template.proposed_msg_template.message;
    
  if (!message) {
    return '';
  }

  let replacements: { [key: string]: string } = {};

  if (isOverdueReminder) {
    if (outstandingInvoices.length === 0) {
      // Use dummy data when no outstanding invoices
      replacements = {
        '{{customer_name}}': composeCustomerName(picName, xeroDirectContactName, customer.name),
        '{{invoice_number}}': 'INV-XXX',
        '{{amount_due}}': '$YYY.00',
        '{{due_date}}': 'DD/MM/YYYY',
        '{{overdue_day_count}}': 'ZZZ',
        '{{employee_name_of_user}}': 'Aria',
        '{{employee_position}}': 'Collection Executive',
        '{{user_name}}': organizationName || 'Your Organization',
        '{{invoice_table}}': 'No overdue invoices found.'
      };
    } else if (useMultipleInvoicesTemplate) {
      // Use multiple invoices template with new od_invoices format
      const totalAmountDue = overdueInvoices.reduce((sum, invoice) =>
        sum + parseFloat(invoice.amount_due || '0'), 0
      );
      
      // Generate od_invoices in the new format: INV-001 ($890.0, 9 Days Overdue), INV-002 ($240.0, 5 Days Overdue)
      const odInvoices = overdueInvoices.map((invoice) => {
        const dueDate = invoice.due_date_at ? new Date(invoice.due_date_at) : null;
        const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
        return `${invoice.invoice_number} ($${parseFloat(invoice.amount_due || '0').toFixed(1)}, ${daysOverdue} Days Overdue)`;
      }).join(', ');

      replacements = {
        '{{customer_name}}': composeCustomerName(picName, xeroDirectContactName, customer.name),
        '{{invoice_number}}': overdueInvoices[0]?.invoice_number || 'INV-XXXX',
        '{{amount_due}}': `$${totalAmountDue.toFixed(2)}`,
        '{{due_date}}': overdueInvoices[0]?.due_date_at ?
          new Date(overdueInvoices[0].due_date_at).toLocaleDateString(undefined, {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }) : 'N/A',
        '{{overdue_day_count}}': overdueInvoices[0]?.due_date_at ?
          Math.max(0, Math.floor((today.getTime() - new Date(overdueInvoices[0].due_date_at).getTime()) / (1000 * 60 * 60 * 24))).toString() : '0',
        '{{employee_name_of_user}}': 'Aria',
        '{{employee_position}}': 'Collection Executive',
        '{{user_name}}': organizationName || 'Your Organization',
        '{{invoice_table}}': generateWhatsAppInvoiceTable(invoices),
        '{{od_invoice_count}}': overdueInvoices.length.toString(),
        '{{od_invoices}}': odInvoices
      };
    } else {
      // Use single invoice template (original logic)
      const targetInvoice = overdueInvoices.length > 0 ? overdueInvoices[0] : outstandingInvoices[0];
      const dueDate = targetInvoice?.due_date_at ? new Date(targetInvoice.due_date_at) : null;
      const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;

      // For WhatsApp messages, show XXX if overdue days is less than 1
      const isWhatsApp = template.proposed_channel?.value?.toLowerCase() === 'whatsapp';
      const overdueDisplay = isWhatsApp && daysOverdue < 1 ? 'XXX' : daysOverdue.toString();

      replacements = {
        '{{customer_name}}': composeCustomerName(picName, xeroDirectContactName, customer.name),
        '{{invoice_number}}': targetInvoice?.invoice_number || 'INV-XXXX',
        '{{amount_due}}': targetInvoice ? `$${parseFloat(targetInvoice.amount_due || '0').toFixed(2)}` : '$0.00',
        '{{due_date}}': targetInvoice?.due_date_at ?
          new Date(targetInvoice.due_date_at).toLocaleDateString(undefined, {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }) : 'N/A',
        '{{overdue_day_count}}': overdueDisplay,
        '{{employee_name_of_user}}': 'Aria',
        '{{employee_position}}': 'Collection Executive',
        '{{user_name}}': organizationName || 'Your Organization'
      };
    }
  } else {
    // For statement of account
    const isEmail = channel === 'email';
    if (outstandingInvoices.length === 0) {
      // Use dummy data when no outstanding invoices
      const dummyInvoiceTable = isEmail 
        ? '<p><strong>STATEMENT OF ACCOUNT:</strong></p><p>No outstanding invoices found.</p>'
        : `\nSTATEMENT OF ACCOUNT:\n` +
          `┌─────────────────┬──────────────┬──────────────┬─────────────┬─────────────┐\n` +
          `│ Invoice Number  │ Invoice Date │ Due Date     │ Amount      │ Days Overdue│\n` +
          `├─────────────────┼──────────────┼──────────────┼─────────────┼─────────────┤\n` +
          `│ INV-XXX         │ DD/MM/YYYY   │ DD/MM/YYYY   │ $YYY.00     │ ZZZ         │\n` +
          `│ INV-XXX         │ DD/MM/YYYY   │ DD/MM/YYYY   │ $YYY.00     │ 0           │\n` +
          `└─────────────────┴──────────────┴──────────────┴─────────────┴─────────────┘`;

      replacements = {
        '{{customer_name}}': composeCustomerName(picName, xeroDirectContactName, customer.name),
        '{{outstanding_invoice_count}}': 'XXX',
        '{{amount_due}}': '$YYY.00',
        '{{invoice_table}}': dummyInvoiceTable,
        '{{employee_name_of_user}}': 'Aria',
        '{{employee_position}}': 'Collection Executive',
        '{{user_name}}': organizationName || 'Your Organization',
        '{{invoice_number}}': 'INV-XXX',
        '{{due_date}}': 'DD/MM/YYYY',
        '{{overdue_day_count}}': 'ZZZ'
      };
    } else {
      // Use actual data when there are outstanding invoices
      const totalAmountDue = outstandingInvoices.reduce((sum, invoice) =>
        sum + parseFloat(invoice.amount_due || '0'), 0
      );
      const invoiceTable = isEmail ? generateInvoiceTableHTML(invoices) : generateInvoiceTable(invoices);

      replacements = {
        '{{customer_name}}': composeCustomerName(picName, xeroDirectContactName, customer.name),
        '{{outstanding_invoice_count}}': outstandingInvoices.length.toString(),
        '{{amount_due}}': `$${totalAmountDue.toFixed(2)}`,
        '{{invoice_table}}': invoiceTable,
        '{{employee_name_of_user}}': 'Aria',
        '{{employee_position}}': 'Collection Executive',
        '{{user_name}}': organizationName || 'Your Organization',
        '{{invoice_number}}': outstandingInvoices[0]?.invoice_number || 'INV-XXXX',
        '{{due_date}}': outstandingInvoices[0]?.due_date_at ?
          new Date(outstandingInvoices[0].due_date_at).toLocaleDateString(undefined, {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }) : 'N/A',
        '{{overdue_day_count}}': outstandingInvoices[0]?.due_date_at ?
          Math.max(0, Math.floor((new Date().getTime() - new Date(outstandingInvoices[0].due_date_at).getTime()) / (1000 * 60 * 60 * 24))).toString() : '0'
      };
    }
  }

  Object.entries(replacements).forEach(([placeholder, value]) => {
    message = message!.replace(new RegExp(escapeRegExp(placeholder), 'g'), value);
  });

  // If email channel and message contains HTML table, convert to full HTML message
  if (channel === 'email' && message.includes('<table')) {
    return convertTextToHTML(message);
  }

  return message;
};

// Convert plain text message to HTML, preserving line breaks and formatting
const convertTextToHTML = (text: string): string => {
  // If message already contains complete HTML blocks (like our table), preserve them
  // Split the message into segments: HTML blocks and plain text
  const segments: Array<{ type: 'html' | 'text'; content: string }> = [];
  
  // Find all HTML blocks - match complete nested div structures or tables
  // Use a more robust function that handles nested divs properly
  const findHtmlBlocks = (str: string): Array<{ start: number; end: number; content: string }> => {
    const blocks: Array<{ start: number; end: number; content: string }> = [];
    let i = 0;
    
    while (i < str.length) {
      // Look for opening div tag
      const divStart = str.indexOf('<div', i);
      if (divStart === -1) break;
      
      // Track depth to find matching closing div
      let depth = 0;
      let start = divStart;
      let j = start + 4;
      
      // Skip to the end of the opening tag
      while (j < str.length && str[j] !== '>') j++;
      if (j >= str.length) break;
      j++; // Skip the '>'
      depth = 1;
      
      // Find the matching closing div
      while (j < str.length && depth > 0) {
        if (str.substring(j, j + 5) === '</div') {
          depth--;
          if (depth === 0) {
            // Find the end of the closing tag
            let k = j + 5;
            while (k < str.length && str[k] !== '>') k++;
            blocks.push({
              start: start,
              end: k + 1,
              content: str.substring(start, k + 1)
            });
            i = k + 1;
            break;
          }
          // Skip to end of closing tag
          let k = j + 5;
          while (k < str.length && str[k] !== '>') k++;
          j = k + 1;
        } else if (str.substring(j, j + 4) === '<div') {
          depth++;
          // Skip to end of opening tag
          let k = j + 4;
          while (k < str.length && str[k] !== '>') k++;
          j = k + 1;
        } else {
          j++;
        }
      }
      
      if (depth > 0) {
        // Didn't find matching closing tag, skip this opening tag
        i = divStart + 4;
      }
    }
    
    return blocks;
  };
  
  const htmlBlocks = findHtmlBlocks(text);
  let lastIndex = 0;
  
  htmlBlocks.forEach((block) => {
    // Add text before HTML block
    if (block.start > lastIndex) {
      const textSegment = text.substring(lastIndex, block.start);
      if (textSegment.trim()) {
        segments.push({ type: 'text', content: textSegment });
      }
    }
    // Add HTML block as-is
    segments.push({ type: 'html', content: block.content });
    lastIndex = block.end;
  });
  
  // Add remaining text after last HTML block
  if (lastIndex < text.length) {
    const textSegment = text.substring(lastIndex);
    if (textSegment.trim()) {
      segments.push({ type: 'text', content: textSegment });
    }
  }
  
  // If no HTML blocks found, process entire text as plain text
  if (segments.length === 0) {
    segments.push({ type: 'text', content: text });
  }
  
  // Build final HTML
  let html = '<div style="font-family: Afacad, sans-serif; font-size: 14px; line-height: 1.3; color: #333;">';
  
  segments.forEach((segment) => {
    if (segment.type === 'html') {
      // Insert HTML block as-is (already properly formatted)
      html += segment.content;
    } else {
      // Convert plain text to HTML paragraphs
      const lines = segment.content.split('\n');
      let inParagraph = false;
      
      lines.forEach((line) => {
        const trimmed = line.trim();
        
        if (trimmed === '') {
          if (inParagraph) {
            html += '</p>';
            inParagraph = false;
          }
          html += '<br>';
        } else {
          if (!inParagraph) {
            html += '<p style="margin: 0 0 3px 0;">';
            inParagraph = true;
          } else {
            html += '<br>';
          }
          html += escapeHtml(trimmed);
        }
      });
      
      if (inParagraph) {
        html += '</p>';
      }
    }
  });
  
  html += '</div>';
  return html;
};

// Extract plain text from HTML message (for email fallback)
export const extractPlainTextFromHTML = (html: string): string => {
  // Remove HTML tags but preserve structure
  let text = html
    // Replace <div> and <p> with newlines
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    // Remove table and convert to text representation
    .replace(/<table[^>]*>/gi, '\n')
    .replace(/<\/table>/gi, '\n')
    .replace(/<thead[^>]*>/gi, '')
    .replace(/<\/thead>/gi, '')
    .replace(/<tbody[^>]*>/gi, '')
    .replace(/<\/tbody>/gi, '')
    .replace(/<tr[^>]*>/gi, '')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<th[^>]*>/gi, '')
    .replace(/<\/th>/gi, ' | ')
    .replace(/<td[^>]*>/gi, '')
    .replace(/<\/td>/gi, ' | ')
    // Remove all other HTML tags
    .replace(/<[^>]+>/g, '')
    // Decode HTML entities
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&nbsp;/g, ' ')
    // Clean up multiple newlines
    .replace(/\n{3,}/g, '\n\n')
    // Trim
    .trim();
  
  return text;
};

// Compose subject line
export const composeSubjectLine = (template: TemplateMessage, customer: CustomerData, invoices: InvoiceData[], organizationName?: string, picName?: string, xeroDirectContactName?: string) => {
  if (!template.proposed_msg_template?.subject_line || !customer) {
    return template.proposed_msg_template?.subject_line || '';
  }

  let subjectLine = template.proposed_msg_template.subject_line;

  // Get customer's invoice data
  const outstandingInvoices = invoices.filter(invoice =>
    invoice.status !== 'PAID' && parseFloat(invoice.amount_due || '0') > 0
  );

  // Check if this is an overdue reminder (single invoice specific)
  const isOverdueReminder = template.reminder_type === 'overdue_reminder';

  let replacements: { [key: string]: string } = {};

  if (isOverdueReminder) {
    if (outstandingInvoices.length === 0) {
      // Use dummy data when no outstanding invoices
      replacements = {
        '{{customer_name}}': customer.name || 'Customer',
        '{{invoice_number}}': 'INV-XXX',
        '{{amount_due}}': '$YYY.00',
        '{{due_date}}': 'DD/MM/YYYY',
        '{{overdue_day_count}}': 'ZZZ',
        '{{employee_name_of_user}}': 'Aria',
        '{{employee_position}}': 'Collection Executive',
        '{{user_name}}': organizationName || 'Your Organization'
      };
    } else {
      // For overdue reminders, use the first (most overdue) invoice data
      const targetInvoice = outstandingInvoices[0];
      const today = new Date();
      const dueDate = targetInvoice?.due_date_at ? new Date(targetInvoice.due_date_at) : null;
      const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;

      replacements = {
        '{{customer_name}}': composeCustomerName(picName, xeroDirectContactName, customer.name),
        '{{invoice_number}}': targetInvoice?.invoice_number || 'INV-XXXX',
        '{{amount_due}}': targetInvoice ? `$${parseFloat(targetInvoice.amount_due || '0').toFixed(2)}` : '$0.00',
        '{{due_date}}': targetInvoice?.due_date_at ?
          new Date(targetInvoice.due_date_at).toLocaleDateString(undefined, {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }) : 'N/A',
        '{{overdue_day_count}}': daysOverdue.toString(),
        '{{employee_name_of_user}}': 'Aria',
        '{{employee_position}}': 'Collection Executive',
        '{{user_name}}': organizationName || 'Your Organization'
      };
    }
  } else {
    // For statement of account
    if (outstandingInvoices.length === 0) {
      // Use dummy data when no outstanding invoices
      replacements = {
        '{{customer_name}}': composeCustomerName(picName, xeroDirectContactName, customer.name),
        '{{outstanding_invoice_count}}': 'XXX',
        '{{amount_due}}': '$YYY.00',
        '{{employee_name_of_user}}': 'Aria',
        '{{employee_position}}': 'Collection Executive',
        '{{user_name}}': organizationName || 'Your Organization',
        '{{invoice_number}}': 'INV-XXX',
        '{{due_date}}': 'DD/MM/YYYY',
        '{{overdue_day_count}}': 'ZZZ'
      };
    } else {
      // Use actual data when there are outstanding invoices
      const totalAmountDue = outstandingInvoices.reduce((sum, invoice) =>
        sum + parseFloat(invoice.amount_due || '0'), 0
      );

      replacements = {
        '{{customer_name}}': composeCustomerName(picName, xeroDirectContactName, customer.name),
        '{{outstanding_invoice_count}}': outstandingInvoices.length.toString(),
        '{{amount_due}}': `$${totalAmountDue.toFixed(2)}`,
        '{{employee_name_of_user}}': 'Aria',
        '{{employee_position}}': 'Collection Executive',
        '{{user_name}}': organizationName || 'Your Organization',
        '{{invoice_number}}': outstandingInvoices[0]?.invoice_number || 'INV-XXXX',
        '{{due_date}}': outstandingInvoices[0]?.due_date_at ?
          new Date(outstandingInvoices[0].due_date_at).toLocaleDateString(undefined, {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }) : 'N/A',
        '{{overdue_day_count}}': outstandingInvoices[0]?.due_date_at ?
          Math.max(0, Math.floor((new Date().getTime() - new Date(outstandingInvoices[0].due_date_at).getTime()) / (1000 * 60 * 60 * 24))).toString() : '0'
      };
    }
  }

  Object.entries(replacements).forEach(([placeholder, value]) => {
    subjectLine = subjectLine.replace(new RegExp(escapeRegExp(placeholder), 'g'), value);
  });

  return subjectLine;
};
